import * as React from "react";
import { ImageOrPlaceholder } from "~/components/products/pdp/ImageOrPlaceholder";
import { cn } from "~/lib/utils";
import type { ProductImage } from "~/data/products/types";

interface ProductImageCarouselProps {
  images: ProductImage[];
}

export function ProductImageCarousel({ images }: ProductImageCarouselProps) {
  const [index, setIndex] = React.useState(0);

  if (images.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-start gap-3 lg:gap-4">
      {/* Fluid aspect-ratio below xl:, since the column's width is fluid until then too. Below lg
          the panel runs edge to edge on phones and the image is framed larger, overflowing it. */}
      <div className="bg-bg-alt flex aspect-[375/240] w-full items-center justify-center overflow-hidden rounded-2xl max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:rounded-none lg:p-2.5 xl:aspect-auto xl:h-[477px]">
        <ImageOrPlaceholder
          src={images[index].src}
          alt={images[index].alt}
          loading="eager"
          fetchPriority="high"
          className="max-lg:h-[133.3%] max-lg:w-[119.2%] max-lg:max-w-none max-lg:shrink-0"
        />
      </div>
      {images.length > 1 && (
        <div className="flex flex-wrap gap-2 max-lg:self-center sm:gap-4 xl:gap-6">
          {images.map((image, i) => (
            <button
              key={image.alt + i}
              type="button"
              aria-label={`Show image ${i + 1}`}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              className={cn(
                // outline, not border — a border shrinks the image inside it via
                // box-sizing: border-box, since the button is exactly 85px including border.
                "size-10 shrink-0 cursor-pointer overflow-hidden rounded-md outline sm:size-16 xl:size-[85px]",
                i === index
                  ? "-outline-offset-2 outline-2 outline-bouncie-500"
                  : "outline-border -outline-offset-[0.5px] outline-[0.5px]",
              )}
            >
              <ImageOrPlaceholder src={image.src} alt={image.alt} className="object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
